/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources, ProductComponent } from '@msdyn365-commerce/components';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IMoeSearchResultContainerResources } from '../moe-search-result-container.props.autogenerated';
import ProductListViewComponent from './product-list-view';

interface IProductSearchResultItems {
    products: ProductSearchResult[];
    context: ICoreContext;
    resources: IMoeSearchResultContainerResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickviewSlot?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    listView?: boolean;
    isOCH?: boolean;
    domainData?: string;
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    products,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    channelInventoryConfigurationId,
    isPriceMinMaxEnabled,
    productsDimensionAvailabilities,
    listView,
    isOCH,
    domainData
}) => {
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparatorInMaori
    };

    if (listView) {
        return (
            <ul className='products-list-view__container'>
                {products.map((product: ProductSearchResult, index: number) => (
                    <li className='product-list-view__item-wrap' key={index}>
                        <ProductListViewComponent
                            isOCH={isOCH}
                            context={context}
                            telemetryContent={telemetryContent}
                            imageSettings={imageSettings}
                            freePriceText={resources.priceFreeInMaori}
                            originalPriceText={resources.originalPriceTextInMaori}
                            currentPriceText={resources.currentPriceTextInMaori}
                            ratingAriaLabel={resources.ratingAriaLabelInMaori}
                            allowBack={allowBackNavigation}
                            id={moduleId}
                            typeName={moduleType}
                            data={{ product }}
                            quickViewButton={quickviewSlot}
                            inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                            priceResources={priceResources}
                            dimensionAvailabilities={productsDimensionAvailabilities?.find(
                                dimensionAvailability =>
                                    ArrayExtensions.hasElements(dimensionAvailability) &&
                                    dimensionAvailability[0].masterProductId === product.MasterProductId
                            )}
                            domainData={domainData}
                        />
                    </li>
                ))}
            </ul>
        );
    } else {
        return (
            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResult, index: number) => (
                    <li className='ms-product-search-result__item' key={index}>
                        <ProductComponent
                            context={context}
                            telemetryContent={telemetryContent}
                            imageSettings={imageSettings}
                            freePriceText={resources.priceFreeInMaori}
                            originalPriceText={resources.originalPriceTextInMaori}
                            currentPriceText={resources.currentPriceTextInMaori}
                            ratingAriaLabel={resources.ratingAriaLabelInMaori}
                            allowBack={allowBackNavigation}
                            id={moduleId}
                            typeName={moduleType}
                            data={{ product }}
                            quickViewButton={quickviewSlot}
                            inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                            priceResources={priceResources}
                            dimensionAvailabilities={productsDimensionAvailabilities?.find(
                                dimensionAvailability =>
                                    ArrayExtensions.hasElements(dimensionAvailability) &&
                                    dimensionAvailability[0].masterProductId === product.MasterProductId
                            )}
                        />
                    </li>
                ))}
            </ul>
        );
    }
};
